/**
 * Procedure Catalog + Doctor Rate Overrides API
 *
 * Backend endpoints (all scoped to the authenticated user's organisation):
 *
 *   Catalog
 *     POST   /api/organisation/procedures
 *     GET    /api/organisation/procedures?category=&include_inactive=
 *     GET    /api/organisation/procedures/:category/:procedure_id
 *     PATCH  /api/organisation/procedures/:category/:procedure_id
 *     DELETE /api/organisation/procedures/:category/:procedure_id  (soft delete)
 *
 *   Doctor rate overrides
 *     GET    /api/organisation/doctors/:doctor_id/rates
 *     PUT    /api/organisation/doctors/:doctor_id/rates/:procedure_id
 *     DELETE /api/organisation/doctors/:doctor_id/rates/:procedure_id
 *     GET    /api/organisation/doctors/:doctor_id/rates/:procedure_id/effective
 *
 * Every function returns `{ success, data?, error? }`. Errors are already
 * translated to user-friendly strings via `buildApiError()`.
 */

import apiClient from './apiClient';

// ── Local error translator ───────────────────────────────────────────
/**
 * Translate an axios error into a user-friendly string.
 * Keeps the server's `detail` message verbatim when available (FastAPI
 * returns `{ detail: "..." }` on 4xx / 422) and falls back to a
 * category-specific default.
 *
 * @param {unknown} error   axios error
 * @param {string}  fallback default message when we can't do better
 * @returns {string}
 */
function buildApiError(error, fallback) {
  const data = error?.response?.data;
  const serverMsg = data?.detail ?? data?.message;
  if (serverMsg) {
    return typeof serverMsg === 'string' ? serverMsg : JSON.stringify(serverMsg);
  }

  switch (error?.response?.status) {
    case 400: return 'Invalid data — please check the form and try again.';
    case 401: return 'Session expired. Please login again.';
    case 403: return 'You do not have permission to perform this action.';
    case 404: return 'The requested item could not be found.';
    case 409: return 'That procedure already exists. Pick a different name or ADA code.';
    case 422: return 'Invalid data — please check the form and try again.';
    case 500: return 'Server error. Please try again in a moment.';
    default:  return fallback;
  }
}

// ══════════════════════════════════════════════════════════════════════
//  Procedure Catalog
// ══════════════════════════════════════════════════════════════════════

/**
 * Create a procedure.
 *
 * POST /api/organisation/procedures
 * @param {Object} body
 * @returns {Promise<{success, data?, error?}>}
 */
export const createProcedure = async (body) => {
  try {
    const response = await apiClient.post('/api/organisation/procedures', body);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[ProcedureAPI] create error:', error);
    return { success: false, error: buildApiError(error, 'Failed to create procedure.') };
  }
};

/**
 * List procedures, optionally filtered by category or including inactive.
 *
 * GET /api/organisation/procedures
 * @param {{ category?: string, include_inactive?: boolean }} [params]
 * @returns {Promise<{success, data?: {procedures: Array, count: number}, error?}>}
 */
export const listProcedures = async ({ category, include_inactive } = {}) => {
  try {
    const params = {};
    if (category && category !== 'all') params.category = category;
    if (include_inactive) params.include_inactive = true;

    const response = await apiClient.get('/api/organisation/procedures', { params });
    return {
      success: true,
      data: {
        procedures: response.data?.procedures || [],
        count: response.data?.count ?? (response.data?.procedures?.length || 0),
      },
    };
  } catch (error) {
    console.error('[ProcedureAPI] list error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load procedures.') };
  }
};

/**
 * A4 / S3 — scoped picker read. List procedures the server considers visible
 * for a visit's department: dept-tagged (active edge) ∪ ORG_WIDE/untagged
 * (C1). A falsy `department_id` OMITS the param → the server returns the full
 * catalog (no filter — parity with the provider picker's falsy anchor).
 *
 * Distinct from the admin `listProcedures` on purpose (RC-1): the picker gets a
 * server-scoped list; the admin catalog stays unscoped. Do NOT collapse the two.
 *
 * GET /api/organisation/procedures?department_id=
 * @param {{ department_id?: string|null, include_inactive?: boolean }} [params]
 * @returns {Promise<{success, data?: {procedures: Array, count: number}, error?}>}
 */
export const listProceduresForDept = async ({ department_id, include_inactive } = {}) => {
  try {
    const params = {};
    if (department_id) params.department_id = department_id;
    if (include_inactive) params.include_inactive = true;

    const response = await apiClient.get('/api/organisation/procedures', { params });
    return {
      success: true,
      data: {
        procedures: response.data?.procedures || [],
        count: response.data?.count ?? (response.data?.procedures?.length || 0),
      },
    };
  } catch (error) {
    console.error('[ProcedureAPI] listForDept error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load procedures.') };
  }
};

/**
 * A4 / S2 — tag a procedure with 0..N departments (C2). Empty array clears all
 * edges → owner_scope reverts to ORG_WIDE; ≥1 → owner_scope=DEPARTMENT.
 * Server rejects (422) an unknown / inactive / foreign department.
 *
 * PUT /api/organisation/procedures/:category/:procedure_id/departments
 * @param {string}   category
 * @param {string}   procedure_id
 * @param {string[]} department_ids
 * @returns {Promise<{success, data?, error?}>}  data = updated ClinicProcedureResponse
 */
export const setProcedureDepartments = async (category, procedure_id, department_ids) => {
  try {
    const response = await apiClient.put(
      `/api/organisation/procedures/${encodeURIComponent(category)}/${encodeURIComponent(procedure_id)}/departments`,
      { department_ids: department_ids || [] }
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[ProcedureAPI] setDepartments error:', error);
    return { success: false, error: buildApiError(error, 'Failed to save departments.') };
  }
};

/**
 * Fetch a single procedure.
 *
 * GET /api/organisation/procedures/:category/:procedure_id
 */
export const getProcedure = async (category, procedure_id) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/procedures/${encodeURIComponent(category)}/${encodeURIComponent(procedure_id)}`
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[ProcedureAPI] get error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load procedure.') };
  }
};

/**
 * Partial update.
 *
 * PATCH /api/organisation/procedures/:category/:procedure_id
 */
export const updateProcedure = async (category, procedure_id, body) => {
  try {
    const response = await apiClient.patch(
      `/api/organisation/procedures/${encodeURIComponent(category)}/${encodeURIComponent(procedure_id)}`,
      body
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[ProcedureAPI] update error:', error);
    return { success: false, error: buildApiError(error, 'Failed to update procedure.') };
  }
};

/**
 * Soft-delete (active=False).
 *
 * DELETE /api/organisation/procedures/:category/:procedure_id
 */
export const deactivateProcedure = async (category, procedure_id) => {
  try {
    await apiClient.delete(
      `/api/organisation/procedures/${encodeURIComponent(category)}/${encodeURIComponent(procedure_id)}`
    );
    return { success: true };
  } catch (error) {
    console.error('[ProcedureAPI] deactivate error:', error);
    return { success: false, error: buildApiError(error, 'Failed to deactivate procedure.') };
  }
};

// ══════════════════════════════════════════════════════════════════════
//  Doctor Rate Overrides
// ══════════════════════════════════════════════════════════════════════

/**
 * Create or update a doctor's override for a procedure.
 *
 * PUT /api/organisation/doctors/:doctor_id/rates/:procedure_id
 * @param {string} doctor_id
 * @param {string} procedure_id
 * @param {number} override_cost  float rupees
 */
export const setDoctorRate = async (doctor_id, procedure_id, override_cost) => {
  try {
    const response = await apiClient.put(
      `/api/organisation/doctors/${encodeURIComponent(doctor_id)}/rates/${encodeURIComponent(procedure_id)}`,
      { override_cost }
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[ProcedureAPI] setDoctorRate error:', error);
    return { success: false, error: buildApiError(error, 'Failed to save override.') };
  }
};

/**
 * List all overrides for a doctor.
 *
 * GET /api/organisation/doctors/:doctor_id/rates
 * @returns {Promise<{success, data?: {rates: Array, count: number}, error?}>}
 */
export const listDoctorRates = async (doctor_id) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/doctors/${encodeURIComponent(doctor_id)}/rates`
    );
    return {
      success: true,
      data: {
        rates: response.data?.rates || [],
        count: response.data?.count ?? (response.data?.rates?.length || 0),
      },
    };
  } catch (error) {
    console.error('[ProcedureAPI] listDoctorRates error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load rate overrides.') };
  }
};

/**
 * Remove an override (revert to clinic default).
 *
 * DELETE /api/organisation/doctors/:doctor_id/rates/:procedure_id
 */
export const deleteDoctorRate = async (doctor_id, procedure_id) => {
  try {
    await apiClient.delete(
      `/api/organisation/doctors/${encodeURIComponent(doctor_id)}/rates/${encodeURIComponent(procedure_id)}`
    );
    return { success: true };
  } catch (error) {
    console.error('[ProcedureAPI] deleteDoctorRate error:', error);
    return { success: false, error: buildApiError(error, 'Failed to clear override.') };
  }
};

/**
 * Effective rate for a doctor/procedure (override or fallback to clinic default).
 *
 * GET /api/organisation/doctors/:doctor_id/rates/:procedure_id/effective
 */
export const getEffectiveRate = async (doctor_id, procedure_id) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/doctors/${encodeURIComponent(doctor_id)}/rates/${encodeURIComponent(procedure_id)}/effective`
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[ProcedureAPI] getEffectiveRate error:', error);
    return { success: false, error: buildApiError(error, 'Failed to load effective rate.') };
  }
};
